import { type ReturnFlow, Step } from "../return-flow";
import type { AgentReturnWindowContext } from "./agent-return-window-context";
import { evaluateAgentReturnWindowPolicy } from "./agent-return-window-policy";
import { getAgentReturnWindowRestriction } from "./agent-return-window-restriction";
import { AgentTimePolicyTier } from "./evaluate-agent-time-policy";

export function getAgentReturnWindowDecision(
  flow: ReturnFlow,
  conditionStep: number,
  itemId: string,
  context: AgentReturnWindowContext,
) {
  const restriction = getAgentReturnWindowRestriction(flow, conditionStep);
  if (!restriction) {
    return undefined;
  }
  const source = context.dates.find((item) => item.itemId === itemId);
  return evaluateAgentReturnWindowPolicy({
    condition: restriction.condition,
    dates: {
      orderedOn: source?.orderedOn
        ? Temporal.Instant.from(source.orderedOn)
        : undefined,
      fulfilledOn: source?.fulfilledOn
        ? Temporal.Instant.from(source.fulfilledOn)
        : undefined,
      deliveredOn: source?.deliveredOn
        ? Temporal.Instant.from(source.deliveredOn)
        : undefined,
    },
    now: Temporal.Instant.from(context.evaluatedAt),
    graceWindows: {
      agentGrace: Temporal.Duration.from({ days: context.agentGraceDays }),
      merchantGrace: Temporal.Duration.from({
        days: context.merchantGraceDays,
      }),
    },
  });
}

/** Adapt only the selected timing gates; the original draft flow stays intact. */
export function applyAgentReturnWindowGrace(
  flow: ReturnFlow,
  itemId: string,
  context: AgentReturnWindowContext,
): ReturnFlow {
  const steps = [...flow.steps];
  for (const [index, step] of flow.steps.entries()) {
    if (step.type !== Step.CONDITION) {
      continue;
    }
    const restriction = getAgentReturnWindowRestriction(flow, index);
    const decision = getAgentReturnWindowDecision(flow, index, itemId, context);
    if (!restriction || decision?.kind !== "evaluated") {
      continue;
    }
    const { tier, policyDeadline, merchantGraceDeadline } = decision.evaluation;
    if (tier === AgentTimePolicyTier.AGENT_GRACE) {
      steps[index] = { ...step, nextFalse: restriction.continueAt };
    } else if (tier === AgentTimePolicyTier.MERCHANT_GRACE) {
      steps[index] = { ...step, nextFalse: steps.length };
      steps.push({
        type: Step.MANUAL_REVIEW,
        next: restriction.continueAt,
        reason: `Commerce Agent return-window exception (${decision.basis}): policy deadline ${policyDeadline}; requested ${context.evaluatedAt}; merchant review cutoff ${merchantGraceDeadline}.`,
      });
    }
  }
  return { ...flow, steps };
}
